import { Controller, useFormContext } from 'react-hook-form';
import type { Control } from 'react-hook-form';
import { ChevronDown } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Label } from '@/components/ui/label';
import { isWritableField } from '@/lib/record-form/schema';
import type { IModuleField, ISectionWithFields } from '@/features/module-builder/types';
import { FieldTypeIcon } from './field-type-icon';
import { RecordFieldInput } from './record-field-input';

export interface RecordFormSectionsProps {
    /** Sections in display order (already sorted by position). */
    sections: ISectionWithFields[];
    /** Prefix for RHF field names (e.g. 'custom.'). */
    namePrefix?: string;
    /** Field NAMES hidden by dependency rules (from useRecordForm). */
    hidden: Set<string>;
    /** Field NAMES currently required (from useRecordForm). */
    required: Set<string>;
    disabled?: boolean;
}

const SECTION_COLUMN_CLASSES: Record<number, string> = {
    1: 'sm:grid-cols-1',
    2: 'sm:grid-cols-2',
    3: 'sm:grid-cols-3',
    4: 'sm:grid-cols-4',
};

const FIELD_SPAN_CLASSES: Record<number, string> = {
    1: 'sm:col-span-1',
    2: 'sm:col-span-2',
    3: 'sm:col-span-3',
    4: 'sm:col-span-4',
};

function noopChange(): void {
    // Non-writable fields render a disabled control that never emits changes.
}

function SectionField({
    field,
    sectionColumns,
    namePrefix,
    isRequired,
    disabled,
    control,
}: {
    field: IModuleField;
    sectionColumns: number;
    namePrefix: string;
    isRequired: boolean;
    disabled?: boolean;
    control: Control<Record<string, unknown>>;
}) {
    const name = `${namePrefix}${field.name}`;
    const span = Math.min(Math.max(field.columnSpan, 1), Math.max(sectionColumns, 1));
    const spanClass = FIELD_SPAN_CLASSES[span] ?? 'sm:col-span-1';

    if (field.fieldType === 'BOOLEAN') {
        return (
            <BooleanField
                field={field}
                name={name}
                spanClass={spanClass}
                isRequired={isRequired}
                disabled={disabled}
                control={control}
            />
        );
    }

    return (
        <div className={cn('flex flex-col gap-2', spanClass)}>
            <Label htmlFor={name} required={isRequired}>
                <FieldTypeIcon fieldType={field.fieldType} className="size-3.5" muted />
                {field.displayName}
            </Label>
            {isWritableField(field) ? (
                <Controller
                    control={control}
                    name={name}
                    render={({ field: rhfField, fieldState }) => (
                        <>
                            <RecordFieldInput
                                field={field}
                                value={rhfField.value}
                                onChange={rhfField.onChange}
                                error={fieldState.error?.message}
                                id={name}
                                disabled={disabled}
                            />
                            {fieldState.error?.message && (
                                <p className="text-xs text-destructive">
                                    {fieldState.error.message}
                                </p>
                            )}
                        </>
                    )}
                />
            ) : (
                <RecordFieldInput
                    field={field}
                    value={undefined}
                    onChange={noopChange}
                    id={name}
                    disabled
                />
            )}
            {field.helpText && <p className="text-xs text-text-secondary">{field.helpText}</p>}
        </div>
    );
}

/**
 * A yes/no field as one row — the question on the left, its switch on the
 * right — rather than a label with a lone switch stranded underneath it.
 */
function BooleanField({
    field,
    name,
    spanClass,
    isRequired,
    disabled,
    control,
}: {
    field: IModuleField;
    name: string;
    spanClass: string;
    isRequired: boolean;
    disabled?: boolean;
    control: Control<Record<string, unknown>>;
}) {
    const isWritable = isWritableField(field);
    return (
        <Controller
            control={control}
            name={name}
            render={({ field: rhfField, fieldState }) => (
                <div
                    className={cn(
                        'border-border-default flex items-center justify-between gap-4 rounded-lg border px-3.5 py-3',
                        spanClass,
                    )}
                >
                    <div className="flex min-w-0 flex-col gap-0.5">
                        <Label htmlFor={name} required={isRequired}>
                            <FieldTypeIcon fieldType={field.fieldType} className="size-3.5" muted />
                            {field.displayName}
                        </Label>
                        {field.helpText && (
                            <p className="text-xs text-text-secondary">{field.helpText}</p>
                        )}
                        {fieldState.error?.message && (
                            <p className="text-xs text-destructive">{fieldState.error.message}</p>
                        )}
                    </div>
                    <RecordFieldInput
                        field={field}
                        value={isWritable ? rhfField.value : undefined}
                        onChange={isWritable ? rhfField.onChange : noopChange}
                        error={fieldState.error?.message}
                        id={name}
                        disabled={disabled || !isWritable}
                    />
                </div>
            )}
        />
    );
}

function SectionBlock({
    section,
    fields,
    namePrefix,
    required,
    disabled,
    control,
}: {
    section: ISectionWithFields;
    fields: IModuleField[];
    namePrefix: string;
    required: Set<string>;
    disabled?: boolean;
    control: Control<Record<string, unknown>>;
}) {
    const columnsClass = SECTION_COLUMN_CLASSES[section.columns] ?? 'sm:grid-cols-1';
    const grid = (
        <div className={cn('grid grid-cols-1 gap-4', columnsClass)}>
            {fields.map((field) => (
                <SectionField
                    key={field.id}
                    field={field}
                    sectionColumns={section.columns}
                    namePrefix={namePrefix}
                    isRequired={required.has(field.name)}
                    disabled={disabled}
                    control={control}
                />
            ))}
        </div>
    );

    // Card shell with a distinct header band — sections must read as clearly
    // separated blocks on every surface (form, record page, previews).
    if (!section.isCollapsible) {
        return (
            <section className="border-border-default bg-bg-surface overflow-hidden rounded-xl border">
                <div className="border-border-default bg-bg-raised/50 border-b px-4 py-3">
                    <h3 className="text-sm font-semibold text-text-heading">
                        {section.displayName}
                    </h3>
                </div>
                <div className="p-4">{grid}</div>
            </section>
        );
    }

    return (
        <Collapsible asChild defaultOpen={!section.isDefaultCollapsed}>
            <section className="border-border-default bg-bg-surface overflow-hidden rounded-xl border">
                <CollapsibleTrigger className="border-border-default bg-bg-raised/50 hover:bg-bg-hover flex w-full cursor-pointer items-center justify-between gap-2 border-b px-4 py-3 text-left transition-colors data-[state=closed]:border-b-0 [&[data-state=open]>svg]:rotate-180">
                    <h3 className="text-sm font-semibold text-text-heading">
                        {section.displayName}
                    </h3>
                    <ChevronDown
                        className="size-4 shrink-0 text-text-tertiary transition-transform"
                        aria-hidden="true"
                    />
                </CollapsibleTrigger>
                <CollapsibleContent className="p-4">{grid}</CollapsibleContent>
            </section>
        </Collapsible>
    );
}

/**
 * Renders a module's sections as labelled field grids wired to the enclosing
 * react-hook-form context (wrap consumers in `<FormProvider {...form}>`).
 * SYSTEM-source fields are skipped — their native UIs own them.
 */
export function RecordFormSections({
    sections,
    namePrefix = '',
    hidden,
    required,
    disabled,
}: RecordFormSectionsProps) {
    const { control } = useFormContext<Record<string, unknown>>();

    const visibleSections = sections
        .map((section) => ({
            section,
            fields: section.fields.filter(
                (field) =>
                    field.isActive && field.fieldSource !== 'SYSTEM' && !hidden.has(field.name),
            ),
        }))
        .filter(({ fields }) => fields.length > 0);

    if (visibleSections.length === 0) return null;

    return (
        <div className="flex flex-col gap-4">
            {visibleSections.map(({ section, fields }) => (
                <SectionBlock
                    key={section.id}
                    section={section}
                    fields={fields}
                    namePrefix={namePrefix}
                    required={required}
                    disabled={disabled}
                    control={control}
                />
            ))}
        </div>
    );
}
